import { hasSelectedEntity } from '../../../../shared/utils/entitySelection';
import { entitySelectionPayload } from '../../../../shared/utils/entitySelection';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import { uploadDocument } from '@/modules/documents/services/documents.service';
import { listStaffResult } from '@/modules/staff/services';
import { listSuppliersResult } from '@/modules/suppliers/services/suppliers.service';
import {
  acceptAssignment,
  completeAssignment,
  createAssignment,
  deleteAssignment,
  getAssignmentsStaticConfig,
  listAssignmentsResult,
  rejectAssignment,
  updateAssignment,
  updateAssignmentClient,
} from '@/modules/deals/services';
import {
  calculateAssignmentsStats,
  getResetAssignmentForm,
  idsMatch,
  includesComparableId,
  isAssignmentOverdue,
  isAssignmentRequestedByCurrentUser,
  isAssignmentAssignedToCurrentUser,
  isSelfAssignedTask,
} from '@/modules/deals/utils';
import { toast } from '../../../../shared/hooks/use-toast';

const { assignmentTypes } = getAssignmentsStaticConfig();

const matchIds = typeof idsMatch === 'function'
  ? idsMatch
  : (left, right) => String(left ?? '') !== '' && String(left) === String(right ?? '');
const listIncludesId = typeof includesComparableId === 'function'
  ? includesComparableId
  : (values = [], value) => Array.isArray(values) && values.some((item) => matchIds(item, value));

const DEFAULT_PERSONAL_FILTERS = {
  query: '',
  clientId: 'all',
  supplierId: 'all',
  dueDate: 'all',
  urgency: 'all',
  staffId: 'all',
};

const ASSIGNMENT_TAB_VALUES = new Set(['da_accettare', 'in_corso', 'completati', 'mie_richieste']);

function mergeLocalOnlyFields(existingAssignment, nextAssignment) {
  return {
    ...nextAssignment,
    comments: existingAssignment?.comments || [],
  };
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function getClientLabel(client) {
  return client?.company || client?.businessName || client?.name || '';
}

function getStaffDisplayName(member) {
  return (
    member?.fullName ||
    member?.name ||
    member?.displayName ||
    [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
    ''
  ).trim();
}

function getAssignmentDueDate(assignment) {
  return assignment.dueDate || assignment.deadline || assignment.scadenza || '';
}

function parseDateOnly(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getTodayDateOnly() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function assignmentMatchesDueDateFilter(assignment, filterValue) {
  if (!filterValue || filterValue === 'all') return true;

  const dueDate = parseDateOnly(getAssignmentDueDate(assignment));
  if (filterValue === 'none') return dueDate === null;
  if (!dueDate) return false;

  const today = getTodayDateOnly();
  if (filterValue === 'overdue') return dueDate < today;
  if (filterValue === 'today') return dueDate.getTime() === today.getTime();
  if (filterValue === 'next_7') return dueDate >= today && dueDate <= addDays(today, 7);
  if (filterValue === 'next_30') return dueDate >= today && dueDate <= addDays(today, 30);

  return true;
}

function assignmentMatchesClientFilter(assignment, clientId, clientsById = {}) {
  if (!clientId || clientId === 'all') return true;

  if (hasSelectedEntity(assignment, 'client', clientId)) return true;

  const client = clientsById[String(clientId)];
  const clientLabel = normalizeText(getClientLabel(client));
  if (!clientLabel) return false;

  return [
    assignment.clientName,
    assignment.client,
    assignment.clientCompany,
    assignment.clientBusinessName,
    assignment.clientSnapshot?.name,
    assignment.clientSnapshot?.company,
    assignment.clientSnapshot?.businessName,
  ].some((value) => normalizeText(value) === clientLabel);
}

function getIdValues(values = []) {
  return values.flatMap((value) => {
    if (value === null || value === undefined || value === '') return [];
    if (typeof value !== 'object') return [value];

    return [
      value.id,
      value.userId,
      value.staffId,
      value.linkedWorkspaceUserId,
      value.workspaceUserId,
    ].filter((item) => item !== null && item !== undefined && item !== '');
  });
}

function getAssignmentWorkspaceInvolvedIds(assignment) {
  return [
    assignment.linkedWorkspaceUserId,
    assignment.workspaceUserId,
    assignment.assigneeId,
    assignment.assignedTo,
    assignment.responsibleId,
    assignment.ownerId,
    ...(Array.isArray(assignment.recipientIds) ? assignment.recipientIds : []),
    ...(Array.isArray(assignment.recipients) ? getIdValues(assignment.recipients) : []),
    ...(Array.isArray(assignment.assigneeIds) ? assignment.assigneeIds : []),
    ...(Array.isArray(assignment.responsibleIds) ? assignment.responsibleIds : []),
  ].filter((value) => value !== null && value !== undefined && value !== '');
}

function getAssignmentStaffInvolvedIds(assignment) {
  return [
    assignment.staffId,
    ...(Array.isArray(assignment.staffIds) ? assignment.staffIds : []),
  ].filter((value) => value !== null && value !== undefined && value !== '');
}

function getAssignmentInvolvedNames(assignment) {
  return [
    assignment.assignee,
    assignment.assigneeName,
    assignment.assignedTo,
    assignment.assignedToName,
    assignment.responsible,
    assignment.responsibleName,
    assignment.owner,
    assignment.ownerName,
    ...(Array.isArray(assignment.recipients) ? assignment.recipients : []),
    ...(Array.isArray(assignment.staffNames) ? assignment.staffNames : []),
    ...(Array.isArray(assignment.assignees) ? assignment.assignees : []),
  ].map(normalizeText).filter(Boolean);
}

function assignmentMatchesStaffFilter(assignment, staffId, staffById = {}) {
  if (!staffId || staffId === 'all') return true;

  const member = staffById[String(staffId)];
  if (!member) {
    return [
      ...getAssignmentWorkspaceInvolvedIds(assignment),
      ...getAssignmentStaffInvolvedIds(assignment),
    ].some((value) => matchIds(value, staffId));
  }

  const memberWorkspaceIds = [
    member.linkedWorkspaceUserId,
    member.workspaceUserId,
    member.userId,
  ].filter((value) => value !== null && value !== undefined && value !== '');
  const memberStaffIds = [member.id].filter((value) => value !== null && value !== undefined && value !== '');

  if (getAssignmentWorkspaceInvolvedIds(assignment).some((value) =>
    memberWorkspaceIds.some((memberId) => matchIds(value, memberId))
  )) {
    return true;
  }

  if (getAssignmentStaffInvolvedIds(assignment).some((value) =>
    memberStaffIds.some((memberId) => matchIds(value, memberId))
  )) {
    return true;
  }

  const memberNames = [getStaffDisplayName(member), member.email, member.workEmail].map(normalizeText).filter(Boolean);
  const assignmentNames = getAssignmentInvolvedNames(assignment);
  return memberNames.some((value) => assignmentNames.includes(value));
}

function getAssignmentFormFromAssignment(assignment) {
  const hasSupplier = assignment.supplierId !== null && assignment.supplierId !== undefined;
  const hasClient = assignment.clientId !== null && assignment.clientId !== undefined;
  return {
    linkType: hasSupplier ? 'supplier' : hasClient ? 'client' : 'none',
    ...entitySelectionPayload(assignment),
    clientId: assignment.clientId ?? '',
    supplierId: assignment.supplierId ?? '',
    supplierName: assignment.supplierName || '',
    title: assignment.title || assignment.description || '',
    description: assignment.description || '',
    recipientIds: [...(assignment.recipientIds || [])],
    recipients: [...(assignment.recipients || [])],
    type: assignment.type || 'altro',
    urgency: assignment.urgency || 'media',
    dueDate: assignment.dueDate || '',
    attachments: [],
    customFields: { ...(assignment.customFields || {}) },
  };
}

function assignmentMatchesPersonalFilters(assignment, filters, { clientsById = {}, staffById = {} } = {}) {
  if (!assignmentMatchesClientFilter(assignment, filters.clientId, clientsById)) {
    return false;
  }

  if (!assignmentMatchesDueDateFilter(assignment, filters.dueDate)) {
    return false;
  }

  if (
    filters.supplierId &&
    filters.supplierId !== 'all' &&
    !hasSelectedEntity(assignment, 'supplier', filters.supplierId)
  ) {
    return false;
  }

  if (filters.urgency && filters.urgency !== 'all' && assignment.urgency !== filters.urgency) {
    return false;
  }

  if (!assignmentMatchesStaffFilter(assignment, filters.staffId, staffById)) {
    return false;
  }

  const query = String(filters.query || '').trim().toLowerCase();
  if (!query) return true;

  return [
    assignment.title,
    assignment.description,
    assignment.clientName,
    assignment.supplierName,
    assignment.sender,
    ...(assignment.recipients || []),
  ].some((value) => String(value || '').toLowerCase().includes(query));
}

export function useAssignmentsData() {
  const { currentUser } = useSession();
  const [searchParams] = useSearchParams();
  const initialAssignmentTab = searchParams.get('assignmentTab');
  const initialStaffFilterId = searchParams.get('staffId') || searchParams.get('personnelId') || '';
  const [assignments, setAssignments] = useState([]);
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [activeTab, setActiveTab] = useState(
    ASSIGNMENT_TAB_VALUES.has(initialAssignmentTab) ? initialAssignmentTab : 'da_accettare'
  );
  const [personalFilters, setPersonalFilters] = useState({
    ...DEFAULT_PERSONAL_FILTERS,
    staffId: initialStaffFilterId || DEFAULT_PERSONAL_FILTERS.staffId,
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [newAssignment, setNewAssignment] = useState(getResetAssignmentForm());
  const [formErrors, setFormErrors] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [assignmentActionStates, setAssignmentActionStates] = useState({});
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [assignmentsModuleDisabled, setAssignmentsModuleDisabled] = useState(false);
  const [assignmentsUnavailable, setAssignmentsUnavailable] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState('');
  const [assignmentsAuthRequired, setAssignmentsAuthRequired] = useState(false);
  const [assignmentsTenantDatabaseBlocked, setAssignmentsTenantDatabaseBlocked] = useState(false);
  const [assignmentsCatalogStatus, setAssignmentsCatalogStatus] = useState('idle');
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [suppliersUnavailable, setSuppliersUnavailable] = useState(false);
  const [suppliersError, setSuppliersError] = useState('');
  const [assignmentCreationWarning, setAssignmentCreationWarning] = useState('');
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const assignmentActionLocksRef = useRef({});
  const createAssignmentLockRef = useRef(false);
  const updateAssignmentLockRef = useRef(false);
  const currentUserId = currentUser?.id ?? null;
  const clientFilterId = searchParams.get('clientId') || '';
  const isClientFiltered = clientFilterId !== '';
  const currentStaff = useMemo(
    () => staff.find((member) =>
      [
        member?.linkedWorkspaceUserId,
        member?.workspaceUserId,
        member?.userId,
      ].some((value) => matchIds(value, currentUserId))
    ) || null,
    [staff, currentUserId]
  );
  const currentRecipientIds = useMemo(
    () => [
      currentUserId,
      currentStaff?.linkedWorkspaceUserId,
      currentStaff?.workspaceUserId,
    ].filter((value) => value !== null && value !== undefined && value !== ''),
    [currentUserId, currentStaff]
  );
  const currentAssignmentUser = useMemo(
    () => ({
      ...(currentUser || {}),
      staffId: currentStaff?.id ?? currentUser?.staffId,
      linkedWorkspaceUserId: currentStaff?.linkedWorkspaceUserId ?? currentUser?.linkedWorkspaceUserId,
      workspaceUserId: currentStaff?.workspaceUserId ?? currentUser?.workspaceUserId,
      recipientIds: currentRecipientIds,
    }),
    [currentRecipientIds, currentStaff, currentUser]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadAssignments() {
      const result = await listAssignmentsResult();

      if (!cancelled) {
        const moduleDisabled = result.error === 'DEALS_MODULE_DISABLED';
        const tenantDatabaseBlocked =
          result.error === 'DEALS_TENANT_DATABASE_NOT_READY' ||
          result.error === 'DEALS_TENANT_DATABASE_METADATA_MISSING' ||
          result.error === 'DEALS_TENANT_DATABASE_UNAVAILABLE';
        setAssignments(result.ok ? result.data.filter((assignment) => assignment.status !== 'rifiutato') : []);
        setIsLoadingAssignments(false);
        setAssignmentsModuleDisabled(moduleDisabled);
        setAssignmentsUnavailable(
          !result.ok && result.error !== 'ASSIGNMENTS_AUTH_REQUIRED' && !moduleDisabled && !tenantDatabaseBlocked
        );
        setAssignmentsAuthRequired(result.error === 'ASSIGNMENTS_AUTH_REQUIRED');
        setAssignmentsTenantDatabaseBlocked(tenantDatabaseBlocked);
        setAssignmentsError(result.ok ? '' : result.message || result.error || '');
        setAssignmentsCatalogStatus(
          result.ok
            ? result.data.length > 0
              ? 'available'
              : 'empty'
            : moduleDisabled
              ? 'module_disabled'
            : tenantDatabaseBlocked
              ? 'tenant_database_unavailable'
            : result.error === 'ASSIGNMENTS_AUTH_REQUIRED'
              ? 'auth_required'
              : 'unavailable'
        );
      }
    }

    loadAssignments();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSuppliersCatalog() {
      const result = await listSuppliersResult();

      if (!cancelled) {
        setSuppliers(result.ok ? result.data : []);
        setSuppliersUnavailable(!result.ok);
        setSuppliersError(result.ok ? '' : result.message || result.error || '');
      }
    }

    loadSuppliersCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadClientsCatalog() {
      const result = await listClientsResult();

      if (!cancelled) {
        setClients(result.ok ? result.data : []);
        setClientsUnavailable(!result.ok);
        setClientsError(result.ok ? '' : result.error || '');
      }
    }

    loadClientsCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStaffCatalog() {
      const result = await listStaffResult();

      if (!cancelled) {
        setStaff(result.ok ? result.data : []);
        setStaffUnavailable(!result.ok);
        setStaffError(result.ok ? '' : result.message || result.error || '');
      }
    }

    loadStaffCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!staffUnavailable) {
      setStaffError('');
    }
  }, [staffUnavailable]);

  const clientsById = useMemo(() => {
    return clients.reduce((acc, client) => {
      if (client?.id !== null && client?.id !== undefined) {
        acc[String(client.id)] = client;
      }

      return acc;
    }, {});
  }, [clients]);

  const staffById = useMemo(() => {
    return staff.reduce((acc, member) => {
      [
        member?.id,
        member?.linkedWorkspaceUserId,
        member?.workspaceUserId,
        member?.userId,
      ].forEach((value) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[String(value)] = member;
        }
      });

      return acc;
    }, {});
  }, [staff]);

  const personalFilterOptions = useMemo(() => {
    return {
      clients: clients
        .map((client) => ({
          value: String(client?.id ?? ''),
          label: getClientLabel(client),
        }))
        .filter((option) => option.value && option.label),
      staff: staff
        .map((member) => ({
          value: String(member?.id ?? member?.linkedWorkspaceUserId ?? member?.userId ?? ''),
          label: getStaffDisplayName(member),
        }))
        .filter((option) => option.value && option.label),
      suppliers: suppliers
        .map((supplier) => ({
          value: String(supplier?.id ?? ''),
          label: supplier?.displayName || supplier?.legalName || supplier?.name || '',
        }))
        .filter((option) => option.value && option.label),
    };
  }, [clients, staff, suppliers]);

  const suppliersById = useMemo(() => {
    return suppliers.reduce((acc, supplier) => {
      if (supplier?.id !== null && supplier?.id !== undefined) {
        acc[String(supplier.id)] = supplier;
      }

      return acc;
    }, {});
  }, [suppliers]);

  const assignmentsWithClientLabels = useMemo(() => {
    return assignments.map((assignment) => {
      const client = assignment.clientId !== null && assignment.clientId !== undefined
        ? clientsById[String(assignment.clientId)]
        : null;

      return {
        ...assignment,
        clientName: getClientLabel(client) || assignment.clientName || assignment.client || '',
        supplierName:
          assignment.supplierName ||
          (assignment.supplierId !== null && assignment.supplierId !== undefined
            ? suppliersById[String(assignment.supplierId)]?.displayName || suppliersById[String(assignment.supplierId)]?.legalName || ''
            : ''),
      };
    });
  }, [assignments, clientsById, suppliersById]);

  const clientFilteredAssignments = useMemo(() => {
    if (!isClientFiltered) return assignmentsWithClientLabels;
    return assignmentsWithClientLabels.filter((assignment) => hasSelectedEntity(assignment, 'client', clientFilterId));
  }, [assignmentsWithClientLabels, clientFilterId, isClientFiltered]);

  const clientFilterLabel = useMemo(() => {
    if (!isClientFiltered) return '';
    return clientsById[String(clientFilterId)]?.name || `Cliente ${clientFilterId}`;
  }, [clientFilterId, clientsById, isClientFiltered]);

  const myRequests = useMemo(() => {
    if (!currentUserId) return [];
    const source = isClientFiltered ? clientFilteredAssignments : assignmentsWithClientLabels;
    return source.filter((assignment) => assignment.status === 'da_accettare' && isAssignmentRequestedByCurrentUser(assignment, currentAssignmentUser) && !isSelfAssignedTask(assignment, currentAssignmentUser));
  }, [assignmentsWithClientLabels, clientFilteredAssignments, currentAssignmentUser, currentUserId, isClientFiltered]);

  const incomingAccepted = useMemo(() => {
    if (!currentUserId) return [];
    const source = isClientFiltered ? clientFilteredAssignments : assignmentsWithClientLabels;
    return source.filter(
      (assignment) =>
        (isAssignmentAssignedToCurrentUser(assignment, currentAssignmentUser) || isAssignmentRequestedByCurrentUser(assignment, currentAssignmentUser)) &&
        assignment.status === 'in_corso'
    );
  }, [assignmentsWithClientLabels, clientFilteredAssignments, currentAssignmentUser, currentUserId, isClientFiltered]);

  const incomingPending = useMemo(() => {
    if (!currentUserId) return [];
    const source = isClientFiltered ? clientFilteredAssignments : assignmentsWithClientLabels;
    return source.filter(
      (assignment) =>
        isAssignmentAssignedToCurrentUser(assignment, currentAssignmentUser) &&
        !isAssignmentRequestedByCurrentUser(assignment, currentAssignmentUser) &&
        assignment.status === 'da_accettare'
    );
  }, [assignmentsWithClientLabels, clientFilteredAssignments, currentAssignmentUser, currentUserId, isClientFiltered]);

  const completedAssignments = useMemo(() => {
    if (!currentUserId) return [];
    const source = isClientFiltered ? clientFilteredAssignments : assignmentsWithClientLabels;
    return source.filter(
      (assignment) =>
        assignment.status === 'completato' &&
        (isAssignmentAssignedToCurrentUser(assignment, currentAssignmentUser) || isAssignmentRequestedByCurrentUser(assignment, currentAssignmentUser))
    );
  }, [assignmentsWithClientLabels, clientFilteredAssignments, currentAssignmentUser, currentUserId, isClientFiltered]);

  const overdueAssignments = useMemo(() => {
    if (isClientFiltered) {
      return clientFilteredAssignments.filter(isAssignmentOverdue);
    }
    if (!currentUserId) return [];
    return assignmentsWithClientLabels.filter(
      (assignment) =>
        isAssignmentOverdue(assignment) &&
        isAssignmentAssignedToCurrentUser(assignment, currentAssignmentUser)
    );
  }, [assignmentsWithClientLabels, clientFilteredAssignments, currentAssignmentUser, currentUserId, currentRecipientIds, isClientFiltered]);

  const filteredMyRequests = useMemo(
    () => myRequests.filter((assignment) => assignmentMatchesPersonalFilters(
      assignment,
      personalFilters,
      { clientsById, staffById }
    )),
    [clientsById, myRequests, personalFilters, staffById]
  );

  const filteredIncomingAccepted = useMemo(
    () => incomingAccepted.filter((assignment) => assignmentMatchesPersonalFilters(
      assignment,
      personalFilters,
      { clientsById, staffById }
    )),
    [clientsById, incomingAccepted, personalFilters, staffById]
  );

  const filteredIncomingPending = useMemo(
    () => incomingPending.filter((assignment) => assignmentMatchesPersonalFilters(
      assignment,
      personalFilters,
      { clientsById, staffById }
    )),
    [clientsById, incomingPending, personalFilters, staffById]
  );

  const filteredCompletedAssignments = useMemo(
    () => completedAssignments.filter((assignment) => assignmentMatchesPersonalFilters(
      assignment,
      personalFilters,
      { clientsById, staffById }
    )),
    [clientsById, completedAssignments, personalFilters, staffById]
  );

  function updatePersonalFilter(key, value) {
    setPersonalFilters((prev) => ({
      ...prev,
      [key]: ['clientId', 'supplierId', 'dueDate', 'urgency', 'staffId'].includes(key) ? value || 'all' : value,
    }));
  }

  function resetPersonalFilters() {
    setPersonalFilters(DEFAULT_PERSONAL_FILTERS);
  }

  const stats = useMemo(() => {
    if (!currentUser) {
      return calculateAssignmentsStats([], { id: null });
    }
    return calculateAssignmentsStats(assignments, currentUser, currentRecipientIds);
  }, [assignments, currentUser, currentRecipientIds]);

  function toggleExpand(assignmentId) {
    setExpandedAssignment((prev) => (prev === assignmentId ? null : assignmentId));
  }

  function openCreateDialog() {
    setNewAssignment(getResetAssignmentForm());
    setFormErrors({});
    setAssignmentCreationWarning('');
    setIsCreateDialogOpen(true);
  }

  function resetCreateDialog() {
    setNewAssignment(getResetAssignmentForm());
    setFormErrors({});
    setAssignmentCreationWarning('');
    setIsCreateDialogOpen(false);
  }

  function openEditDialog(assignment) {
    setAssignmentToEdit(assignment);
    setNewAssignment(getAssignmentFormFromAssignment(assignment));
    setFormErrors({});
    setAssignmentCreationWarning('');
    setIsEditDialogOpen(true);
  }

  function resetEditDialog() {
    setAssignmentToEdit(null);
    setNewAssignment(getResetAssignmentForm());
    setFormErrors({});
    setIsEditDialogOpen(false);
  }

  function updateNewAssignmentField(field, value) {
    setNewAssignment((prev) => ({
      ...prev,
      ...(field === 'linkType' && value === 'client' ? { supplierId: '', supplierName: '' } : {}),
      ...(field === 'linkType' && value === 'supplier' ? { clientId: '' } : {}),
      ...(field === 'linkType' && value === 'none' ? { clientId: '', supplierId: '', supplierName: '' } : {}),
      [field]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  }

  async function uploadStagedAssignmentAttachments(assignment, files = []) {
    if (!files.length) return [];

    const client = assignment.clientId ? clientsById[String(assignment.clientId)] : null;
    const uploadResults = await Promise.all(
      files.map((file) =>
        uploadDocument({
          file,
          form: {
            assignmentId: assignment.id,
            clientId: assignment.clientId || '',
            client: client?.name || assignment.supplierName || 'Task senza cliente',
            uploadedBy: currentUser?.name || currentUser?.email || 'Utente',
            category: 'Allegato incarico',
            status: 'in_revisione',
          },
        })
      )
    );

    return uploadResults.filter((result) => !result.ok);
  }

  function toggleRecipient(recipientId) {
    setNewAssignment((prev) => ({
      ...prev,
      recipientIds: prev.recipientIds.map(String).includes(String(recipientId))
        ? prev.recipientIds.filter((id) => String(id) !== String(recipientId))
        : [...prev.recipientIds, String(recipientId)],
    }));

    setFormErrors((prev) => ({
      ...prev,
      recipientIds: '',
    }));
  }

  function requestDeleteAssignment(assignment) {
    setAssignmentToDelete(assignment);
    setIsDeleteDialogOpen(true);
  }

  async function confirmDeleteAssignment() {
    if (!assignmentToDelete) return;

    const result = await deleteAssignment(assignmentToDelete.id);
    if (!result.ok) return result;

    setAssignments((prev) => prev.filter((assignment) => assignment.id !== assignmentToDelete.id));
    setAssignmentToDelete(null);
    setIsDeleteDialogOpen(false);
    return result;
  }

  function cancelDeleteAssignment() {
    setAssignmentToDelete(null);
    setIsDeleteDialogOpen(false);
  }

  async function handleAcceptAssignment(assignmentId) {
    if (assignmentActionLocksRef.current[assignmentId] || assignmentActionStates[assignmentId]?.loading) {
      return { ok: false, error: 'ASSIGNMENT_ACTION_PENDING' };
    }

    assignmentActionLocksRef.current[assignmentId] = true;
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: true, action: 'accept', error: '' },
    }));
    const result = await acceptAssignment(assignmentId);
    if (!result.ok) {
      setAssignmentActionStates((prev) => ({
        ...prev,
        [assignmentId]: {
          loading: false,
          action: 'accept',
          error: result.message || 'Task non accettato.',
        },
      }));
      assignmentActionLocksRef.current[assignmentId] = false;
      return result;
    }

    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? mergeLocalOnlyFields(assignment, result.data.assignment)
          : assignment
      )
    );
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: false, action: '', error: '' },
    }));
    assignmentActionLocksRef.current[assignmentId] = false;
    return result;
  }

  async function handleRejectAssignment(assignmentId) {
    if (assignmentActionLocksRef.current[assignmentId] || assignmentActionStates[assignmentId]?.loading) {
      return { ok: false, error: 'ASSIGNMENT_ACTION_PENDING' };
    }

    assignmentActionLocksRef.current[assignmentId] = true;
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: true, action: 'reject', error: '' },
    }));
    const result = await rejectAssignment(assignmentId);
    if (!result.ok) {
      setAssignmentActionStates((prev) => ({
        ...prev,
        [assignmentId]: {
          loading: false,
          action: 'reject',
          error: result.message || 'Task non rifiutato.',
        },
      }));
      assignmentActionLocksRef.current[assignmentId] = false;
      return result;
    }

    setAssignments((prev) => prev.filter((assignment) => assignment.id !== assignmentId));
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: false, action: '', error: '' },
    }));
    assignmentActionLocksRef.current[assignmentId] = false;
    return result;
  }

  async function handleCompleteAssignment(assignmentId) {
    if (assignmentActionLocksRef.current[assignmentId] || assignmentActionStates[assignmentId]?.loading) {
      return { ok: false, error: 'ASSIGNMENT_ACTION_PENDING' };
    }

    assignmentActionLocksRef.current[assignmentId] = true;
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: true, action: 'complete', error: '' },
    }));
    const result = await completeAssignment(assignmentId);
    if (!result.ok) {
      setAssignmentActionStates((prev) => ({
        ...prev,
        [assignmentId]: {
          loading: false,
          action: 'complete',
          error: result.message || 'Task non completato.',
        },
      }));
      assignmentActionLocksRef.current[assignmentId] = false;
      return result;
    }

    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? mergeLocalOnlyFields(assignment, result.data.assignment)
          : assignment
      )
    );
    setAssignmentActionStates((prev) => ({
      ...prev,
      [assignmentId]: { loading: false, action: '', error: '' },
    }));
    assignmentActionLocksRef.current[assignmentId] = false;
    return result;
  }

  async function handleUpdateAssignmentClient(assignmentId, clientId) {
    const result = await updateAssignmentClient(assignmentId, clientId);
    if (!result.ok) return result;

    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentId
          ? mergeLocalOnlyFields(assignment, result.data.assignment)
          : assignment
      )
    );
    return result;
  }

  async function handleUpdateAssignment() {
    if (!assignmentToEdit) return null;
    if (updateAssignmentLockRef.current) {
      return { ok: false, error: 'ASSIGNMENT_UPDATE_PENDING' };
    }

    updateAssignmentLockRef.current = true;
    setIsSubmittingAssignment(true);
    let result;
    try {
      result = await updateAssignment(assignmentToEdit.id, newAssignment, staff);
    } finally {
      updateAssignmentLockRef.current = false;
      setIsSubmittingAssignment(false);
    }

    if (!result.ok) {
      setFormErrors(result.data?.formErrors || {});
      return result;
    }

    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === assignmentToEdit.id
          ? mergeLocalOnlyFields(assignment, result.data.assignment)
          : assignment
      )
    );
    resetEditDialog();
    return result;
  }

  async function updateAssignmentInline(draft) {
    const result = await updateAssignment(draft.id, draft, staff);
    if (result.ok) {
      setAssignments((current) => current.map((item) => item.id === draft.id ? mergeLocalOnlyFields(item, result.data.assignment) : item));
    }
    return result;
  }

  function updateCommentDraft(assignmentId, value) {
    setCommentDrafts((prev) => ({
      ...prev,
      [assignmentId]: value,
    }));
  }

  function publishComment(assignmentId) {
    return {
      ok: false,
      error: 'ASSIGNMENT_COMMENTS_COMPONENT_HANDLES_SUBMIT',
      message: 'I commenti vengono gestiti dalla scheda task.',
    };
  }

  async function handleCreateAssignment() {
    if (createAssignmentLockRef.current) {
      return { ok: false, error: 'ASSIGNMENT_CREATE_PENDING' };
    }

    createAssignmentLockRef.current = true;
    setIsSubmittingAssignment(true);
    let result;
    try {
      result = await createAssignment(newAssignment, staff, currentUser);

      if (!result.ok) {
        setFormErrors(result.data?.formErrors || {});
        return result;
      }

      const failedUploads = await uploadStagedAssignmentAttachments(
        result.data.assignment,
        newAssignment.attachments || []
      );
      const warning = failedUploads.length
        ? failedUploads.length === (newAssignment.attachments || []).length
          ? 'Task creata, ma gli allegati non sono stati caricati.'
          : 'Task creata, ma alcuni allegati non sono stati caricati.'
        : '';

      setAssignments((prev) => [result.data.assignment, ...prev]);
      if (result.data.assignment.status === 'in_corso' && isSelfAssignedTask(result.data.assignment, currentAssignmentUser)) {
        setActiveTab('in_corso');
      }
      resetCreateDialog();
      if (warning) {
        setAssignmentCreationWarning(warning);
        toast({ title: 'Allegati non caricati', description: warning, variant: 'destructive' });
        return {
          ...result,
          warning,
        };
      }

      return result;
    } finally {
      createAssignmentLockRef.current = false;
      setIsSubmittingAssignment(false);
    }
  }

  return {
    assignments,
    teamAssignments: assignmentsWithClientLabels,
    currentUser: currentAssignmentUser,
    staff,
    clients,
    suppliers,
    isLoadingAssignments,
    assignmentsModuleDisabled,
    assignmentsUnavailable,
    assignmentsError,
    assignmentsAuthRequired,
    assignmentsTenantDatabaseBlocked,
    assignmentsCatalogStatus,
    staffUnavailable,
    staffError,
    clientsUnavailable,
    clientsError,
    suppliersUnavailable,
    suppliersError,
    assignmentCreationWarning,
    isSubmittingAssignment,
    isClientFiltered,
    clientFilterId,
    clientFilterLabel,
    clientFilteredAssignments,
    staffCatalogStatus: staffUnavailable ? 'unavailable' : staff.length > 0 ? 'available' : 'empty',
    clientsCatalogStatus: clientsUnavailable ? 'unavailable' : clients.length > 0 ? 'available' : 'empty',
    suppliersCatalogStatus: suppliersUnavailable ? 'unavailable' : suppliers.length > 0 ? 'available' : 'empty',
    assignmentTypes,
    expandedAssignment,
    activeTab,
    setActiveTab,
    personalFilters,
    personalFilterOptions,
    updatePersonalFilter,
    resetPersonalFilters,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    assignmentToDelete,
    assignmentToEdit,
    newAssignment,
    formErrors,
    commentDrafts,
    assignmentActionStates,
    myRequests,
    incomingAccepted: filteredIncomingAccepted,
    incomingPending: filteredIncomingPending,
    completedAssignments: filteredCompletedAssignments,
    unfilteredIncomingAccepted: incomingAccepted,
    unfilteredIncomingPending: incomingPending,
    unfilteredCompletedAssignments: completedAssignments,
    filteredMyRequests,
    overdueAssignments,
    stats,
    toggleExpand,
    openCreateDialog,
    resetCreateDialog,
    updateNewAssignmentField,
    toggleRecipient,
    openEditDialog,
    resetEditDialog,
    requestDeleteAssignment,
    confirmDeleteAssignment,
    cancelDeleteAssignment,
    handleAcceptAssignment,
    handleRejectAssignment,
    handleCompleteAssignment,
    handleUpdateAssignmentClient,
    handleUpdateAssignment,
    updateAssignmentInline,
    updateCommentDraft,
    publishComment,
    handleCreateAssignment,
  };
}
